import React, { useEffect, useMemo, useRef } from 'react';
import {
  FormField,
  DatePicker,
  Layout,
  Cell,
  TimeInput,
} from '@wix/design-system';
import { BaseInputProps, BaseInputRef } from '../BaseInput';
import { Field } from '../Field';
import { dataHooks } from '../dataHooks';
import { useCustomFieldsFormContext } from '../../components/CustomFieldsFormContext';

export interface DateTimeProps extends BaseInputProps<string> {}

export const DateTime = ({
  dataHook,
  schema,
  initialValue,
  inputRef,
  onChange,
}: DateTimeProps) => {
  const date = useMemo(
    () => (initialValue ? new Date(initialValue) : undefined),
    [initialValue],
  );

  const ref = useRef<BaseInputRef>({});
  const dateRef = useRef<BaseInputRef>({});
  const timeRef = useRef<BaseInputRef>({});

  const formState = useCustomFieldsFormContext();

  useEffect(() => {
    ref.current.focus = () => {
      if (dateRef.current.invalid) {
        return dateRef.current.focus?.();
      }

      return timeRef.current.focus?.();
    };

    inputRef?.(ref.current);
  }, []);

  return (
    <Field dataHook={dataHook} type={schema.type}>
      <FormField label={schema.label} dataHook={dataHooks.Label}>
        <Layout>
          <Cell span={6}>
            <DatePicker
              ref={(internalRef) => {
                dateRef.current.focus = () =>
                  // TODO: PR to WSR needed to expose as public API
                  // @ts-expect-error
                  internalRef?.state?.inputRef?.focus?.();
              }}
              width="100%"
              value={date}
              validate
              onValidate={(payload) => {
                dateRef.current.invalid = payload.validationType !== 'valid';
                ref.current.invalid =
                  dateRef.current.invalid || timeRef.current.invalid;
              }}
              onChange={(newValue: Date) => onChange(newValue.toISOString())}
              dataHook={`${dataHooks.Input}-date`}
            />
          </Cell>
          <Cell span={6}>
            <TimeInput
              ref={(internalRef) => {
                timeRef.current.focus = () => internalRef?.focus();
              }}
              dataHook={`${dataHooks.Input}-time`}
              value={date}
              invalidMessage={formState._translate(
                'cairo.customFields.fieldType.dateAndTime.invalid.value',
              )}
              onInvalid={() => {
                timeRef.current.invalid = true;
                ref.current.invalid =
                  dateRef.current.invalid || timeRef.current.invalid;
              }}
              onChange={({ date: newValue }) => {
                timeRef.current.invalid = false;
                ref.current.invalid =
                  dateRef.current.invalid || timeRef.current.invalid;

                if (newValue) {
                  onChange(newValue.toISOString());
                }
              }}
            />
          </Cell>
        </Layout>
      </FormField>
    </Field>
  );
};
